import { Network } from "@capacitor/network";
import { Preferences } from "@capacitor/preferences";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { onlineManager } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import React from "react";
import { shouldRetryPillarQuery } from "shared/api/client/query-client";
import { queryClient } from "hub/src/api/client/query-client";
import type { PlatformSpecificQueryClientProvider } from "./types";

interface AsyncStorage {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<unknown>;
  removeItem: (key: string) => Promise<void>;
}

export const setupNativePlatformQueryClientProvider = () => {
  const CACHE_TIME = 1000 * 60 * 60 * 24 * 7; // 7 days

  // Updates default queryClient configuration to enable caching
  queryClient.setDefaultOptions({
    queries: {
      cacheTime: CACHE_TIME,
      staleTime: 1000 * 2, // 2 seconds
      retry: shouldRetryPillarQuery,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      networkMode: "offlineFirst", // Critical for offline support
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  });

  const preferencesStorage: AsyncStorage = {
    async getItem(key: string) {
      const { value } = await Preferences.get({ key });
      return value;
    },
    async setItem(key: string, value: string) {
      await Preferences.set({ key, value });
    },
    async removeItem(key: string) {
      await Preferences.remove({ key });
    },
  };

  const persister = createAsyncStoragePersister({
    storage: preferencesStorage,
    throttleTime: 1000,
  });

  // Sets up network monitoring
  onlineManager.setEventListener((setOnline) => {
    const promise = Network.addListener("networkStatusChange", (status) => {
      setOnline(status.connected);
    });

    return () => {
      promise.then((listener) => {
        listener.remove();
      });
    };
  });

  const NativePlatformQueryClientProvider: PlatformSpecificQueryClientProvider =
    (props) => {
      return (
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: CACHE_TIME,
            dehydrateOptions: {
              dehydrateQueries: true,
              shouldDehydrateQuery: (query) => {
                return query.state.status === "success" || !!query?.state?.data;
              },
              dehydrateMutations: true, // Essential for offline mutations
            },
          }}
          onSuccess={() => {
            // resume mutations after initial restore from localStorage was successful
            queryClient.resumePausedMutations().then(() => {
              queryClient.invalidateQueries();
            });
          }}
        >
          {props.children}
        </PersistQueryClientProvider>
      );
    };

  return NativePlatformQueryClientProvider;
};
